import { useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { DashFeaturesEnum } from "dash-common";
import {
  Button,
  FilterContext,
  ModalContext,
  PaginationContext,
  toast,
  useCheckIfSubscriptionFeatureActiveBroker,
  useServerResponseTranslation,
} from "dash-ui";

import {
  GetAlertItemsDocument,
  useGetAlertConfigsCountQuery,
  useGetAlertItemsQuery,
  useObserveBrokerAlertItemChangesSubscription,
  useSetHiddenStateToAlertItemMutation,
} from "gql/generated.graphql";
import PropertyCard from "containers/properties/PropertyCard/PropertyCard";

import { StyledList } from "./AlertPropertiesList.styles";

const AlertPropertiesList = () => {
  const { t } = useTranslation(["common", "pages"]);
  const { setTotal, state, getRefetchProps, setPerPage } =
    useContext(PaginationContext);
  const pagination = getRefetchProps(state);
  const { setPayload, toggleModal } = useContext(ModalContext);
  const { state: filterState } = useContext(FilterContext);
  const translateErrorMessage = useServerResponseTranslation();
  const { checkFeatureAccess } = useCheckIfSubscriptionFeatureActiveBroker([
    DashFeaturesEnum.DOWNLOAD_UNBRANDED_CONTENT,
  ]);

  const variables = useMemo(
    () => ({
      pagination,
      filter: {
        ...(filterState.filter ? filterState.filter : {}),
        isHidden: filterState.filter?.isHidden ? undefined : false,
      },
      sort: filterState.sort || undefined,
    }),
    [filterState, pagination]
  );

  const { data, refetch, loading } = useGetAlertItemsQuery({
    variables: variables,
    fetchPolicy: "network-only",
  });

  const { data: dataCount, loading: dataCountLoading } =
    useGetAlertConfigsCountQuery({
      variables: { pagination: { limit: 1 } },
      fetchPolicy: "network-only",
    });

  const { alertConfigsCount } = useMemo(
    () => ({
      alertConfigsCount: dataCount?.getAlertConfigs.totalCount || 0,
    }),
    [dataCount]
  );

  useObserveBrokerAlertItemChangesSubscription({
    onData() {
      refetch();
    },
  });

  const [setHiddenStateToAlertItemMutation, { loading: isMutationLoading }] =
    useSetHiddenStateToAlertItemMutation();

  const { alerts, totalCount } = useMemo(
    () => ({
      alerts: data?.getAlertItems.collection || [],
      totalCount: data?.getAlertItems.totalCount || 0,
    }),
    [data]
  );

  const onHideProperty = useCallback(
    (alertItem: { _id: string; isHidden: boolean }) =>
      (e: { stopPropagation: () => void }) => {
        e.stopPropagation();
        setHiddenStateToAlertItemMutation({
          variables: {
            alertItemId: alertItem._id,
            isHidden: !alertItem.isHidden,
          },
          refetchQueries: [
            {
              query: GetAlertItemsDocument,
              variables: variables,
              fetchPolicy: "network-only",
            },
          ],
          onCompleted: () =>
            !alertItem.isHidden
              ? toast.success(t("common:alerts:alertWasHidden"))
              : toast.success(t("common:alerts:alertWasShown")),
          onError: (error) => toast.error(translateErrorMessage(error)),
        });
      },
    [setHiddenStateToAlertItemMutation, translateErrorMessage, variables, t]
  );

  useEffect(() => {
    setTotal(totalCount);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [totalCount]);

  useEffect(() => {
    setPerPage(12);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <StyledList
      paginationProps={{
        nextButtonLabel: t("common:labels.next"),
        previousButtonLabel: t("common:labels.previous"),
      }}
      listProps={{
        w: "100%",
        loading: loading || dataCountLoading,
        placeholderProps: alertConfigsCount
          ? {
              label: loading ? "" : t("pages:propertyPage.noProperties") || "",
            }
          : {
              title: loading
                ? ""
                : t`pages:alertPage:labels:noPropertiesPlaceholderTitle` || "",
              label: loading
                ? ""
                : t`pages:alertPage:labels:noPropertiesPlaceholder` || "",
              buttonProps: !loading
                ? {
                    children: t`pages:alertPage:labels:createAlertTitle` || "",
                    px: [2, 3, 10, 10],
                    icon: "plus",
                    onClick: () => toggleModal(true, "AddAlertModal"),
                  }
                : undefined,
            },
        loadingProps: { bgColor: "inherit" },
        grid: [343, 251, 356, 356],
        data: alerts,
        gap: 16,
        mb: 2,
        mt: 6,
      }}
    >
      {(item) => (
        <PropertyCard
          property={item.property}
          label={item.brokerAlertConfig.name}
          isHidden={item.isHidden}
          renderOptions={() => (
            <>
              <Button
                square
                icon="folder"
                onClick={(e) => {
                  e.stopPropagation();
                  if (checkFeatureAccess()) {
                    setPayload({ propertyId: item.property._id });
                    toggleModal(true, "AddPropertyToPortfolio");
                  }
                }}
              />
              <Button
                square
                ml={2}
                isLoading={isMutationLoading}
                disabled={isMutationLoading}
                colorVariation="danger"
                icon={!item.isHidden ? "hide" : "show"}
                onClick={onHideProperty(item)}
              />
            </>
          )}
        />
      )}
    </StyledList>
  );
};

export default AlertPropertiesList;
